import { Controller, useFormContext } from 'react-hook-form';

import { CollapsiblePanel } from '../../../../components/Panels/shared/CollapsiblePanel/CollapsiblePanel';
import { CollapsiblePanelProvider } from '../../../../components/Panels/shared/CollapsiblePanel/CollapsiblePanel.context';
import { FinanceSelect } from '../../../../financeProviders/shared/FinanceSelect';
import { FinanceTextField } from '../../../../financeProviders/shared/FinanceTextField/FinanceTextField';
import { Label } from '../../../../../Toolkit/Inputs/Label';
import { MobilePhoneNumberInput } from '../../../../components/Panels/shared/MobilePhoneNumberInput/MobilePhoneNumberInput';

import type {
  Form,
  CloseBrothersForm,
} from '../../../../queries/schema/carFinance.schema';

import { Spacer } from '../../../../../../helpers/genericStyles';

/*
 * We avoid using the same names for constants in each of the employment panels
 * so that sonarcloud does not incorrectly mark these blocks as duplicates.
 */

/*
 * Ideally, these hardcoded values would come from the server.
 *
 * However, the legacy APIs used for car finance do not provide these values.
 * Furthermore, no changes are planned to these APIs unless absolutely necessary.
 *
 * We must therefore wait until these APIs are migrated to the new platform
 * before we may update them to include these values.
 */
const CLOSE_BROTHERS_EMPLOYMENT_STATUS = [
  {
    label: 'Select employment status',
    value: '',
  },
  'Permanent',
  'Temporary',
  'Contract',
];

const INDUSTRY = [
  {
    label: 'Select industry category',
    value: '',
  },
  'Accounting',
  'Airlines/Aviation',
  'Alternative Dispute Resolution',
  'Alternative Medicine',
  'Animation',
  'Apparel/Fashion',
  'Architecture/Planning',
  'Arts/Crafts',
  'Automotive',
  'Aviation/Aerospace',
  'Banking/Mortgage',
  'Biotechnology/Greentech',
  'Broadcast Media',
  'Building Materials',
  'Business Supplies/Equipment',
  'Capital Markets/Hedge Fund/Private Equity',
  'Chemicals',
  'Civic/Social Organization',
  'Civil Engineering',
  'Commercial Real Estate',
  'Computer Games',
  'Computer Hardware',
  'Computer Networking',
  'Computer Software/Engineering',
  'Computer/Network Security',
  'Construction',
  'Consumer Electronics',
  'Consumer Goods',
  'Consumer Services',
  'Cosmetics',
  'Dairy',
  'Defense/Space',
  'Design',
  'E-Learning',
  'Education Management',
  'Electrical/Electronic Manufacturing',
  'Entertainment/Movie Production',
  'Environmental Services',
  'Events Services',
  'Executive Office',
  'Facilities Services',
  'Farming',
  'Financial Services',
  'Fine Art',
  'Fishery',
  'Food Production',
  'Food/Beverages',
  'Fundraising',
  'Furniture',
  'Gambling/Casinos',
  'Glass/Ceramics/Concrete',
  'Government Administration',
  'Government Relations',
  'Graphic Design/Web Design',
  'Health/Fitness',
  'Higher Education/Acadamia',
  'Hospital/Health Care',
  'Hospitality',
  'Human Resources/HR',
  'Import/Export',
  'Individual/Family Services',
  'Industrial Automation',
  'Information Services',
  'Information Technology/IT',
  'Insurance',
  'International Affairs',
  'International Trade/Development',
  'Internet',
  'Investment Banking/Venture',
  'Investment Management/Hedge Fund/Private Equity',
  'Judiciary',
  'Law Enforcement',
  'Law Practice/Law Firms',
  'Legal Services',
  'Legislative Office',
  'Leisure/Travel',
  'Library',
  'Logistics/Procurement',
  'Luxury Goods/Jewelry',
  'Machinery',
  'Management Consulting',
  'Maritime',
  'Market Research',
  'Marketing/Advertising/Sales',
  'Mechanical or Industrial Engineering',
  'Media Production',
  'Medical Equipment',
  'Medical Practice',
  'Mental Health Care',
  'Military Industry',
  'Mining/Metals',
  'Motion Pictures/Film',
  'Museums/Institutions',
  'Music',
  'Nanotechnology',
  'Newspapers/Journalism',
  'Non-Profit/Volunteering',
  'Oil/Energy/Solar/Greentech',
  'Online Publishing',
  'Other Industry',
  'Outsourcing/Offshoring',
  'Package/Freight Delivery',
  'Packaging/Containers',
  'Paper/Forest Products',
  'Performing Arts',
  'Pharmaceuticals',
  'Philanthropy',
  'Photography',
  'Plastics',
  'Political Organization',
  'Primary/Secondary Education',
  'Printing',
  'Professional Training',
  'Program Development',
  'Public Relations/PR',
  'Public Safety',
  'Publishing Industry',
  'Railroad Manufacture',
  'Ranching',
  'Real Estate/Mortgage',
  'Recreational Facilities/Services',
  'Religious Institutions',
  'Renewables/Environment',
  'Research Industry',
  'Restaurants',
  'Retail Industry',
  'Security/Investigations',
  'Semiconductors',
  'Shipbuilding',
  'Sporting Goods',
  'Sports',
  'Staffing/Recruiting',
  'Supermarkets',
  'Telecommunications',
  'Textiles',
  'Think Tanks',
  'Tobacco',
  'Translation/Localization',
  'Transportation',
  'Utilities',
  'Venture Capital/VC',
  'Veterinary',
  'Warehousing',
  'Wholesale',
  'Wine/Spirits',
  'Wireless',
  'Writing/Editing',
];

const EMPLOYMENT_TYPE = [
  {
    label: 'Select employment type',
    value: '',
  },
  'Full-time',
  'Part-time',
  'Self-employed',
];

const CLOSE_BROTHERS_TIME_AT_EMPLOYER = [
  {
    label: 'Select time',
    value: '',
  },
  { value: '0', label: '0 years' },
  { value: '1', label: '1 year' },
  { value: '2', label: '2 years' },
  { value: '3', label: '3 years' },
  { value: '4', label: '4 years' },
  { value: '5', label: '5 years' },
  { value: '6', label: '6 years' },
  { value: '7', label: '7 years' },
  { value: '8', label: '8 years' },
  { value: '9', label: '9 years' },
  { value: '10', label: '10+ years' },
];

const EmploymentDetails = () => {
  const { control } = useFormContext<CloseBrothersForm>();

  return (
    <CollapsiblePanelProvider panelName="employmentDetails">
      <CollapsiblePanel
        headerText="Employment Details"
        accordionName="Employment Details"
        mb={['S8', 'M16']}
        aria-label="Employment Details"
        panelName="employmentDetails"
      >
        <Spacer marginBottom={['M16', 'M24']} />
        <Label htmlFor="employmentDetails.industry">Industry Category*</Label>
        <Controller
          control={control}
          name="employmentDetails.industry"
          render={({ field, fieldState }) => (
            <FinanceSelect
              {...field}
              {...fieldState}
              placeholderAsOption
              options={INDUSTRY}
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="employmentDetails.occupation">Occupation*</Label>
        <Controller
          control={control}
          name="employmentDetails.occupation"
          render={({ field, fieldState }) => (
            <FinanceTextField
              {...field}
              {...fieldState}
              placeholder="Enter occupation"
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="employmentDetails.type">Employment Type*</Label>
        <Controller
          control={control}
          name="employmentDetails.type"
          render={({ field, fieldState }) => (
            <FinanceSelect
              {...field}
              {...fieldState}
              placeholderAsOption
              options={EMPLOYMENT_TYPE}
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="employmentDetails.status">Employment Status*</Label>
        <Controller
          control={control}
          name="employmentDetails.status"
          render={({ field, fieldState }) => (
            <FinanceSelect
              {...field}
              {...fieldState}
              placeholderAsOption
              options={CLOSE_BROTHERS_EMPLOYMENT_STATUS}
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="employmentDetails.employerName">Employer Name*</Label>
        <Controller
          control={control}
          name="employmentDetails.employerName"
          render={({ field, fieldState }) => (
            <FinanceTextField
              {...field}
              {...fieldState}
              placeholder="Enter employer name"
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <MobilePhoneNumberInput<Form>
          fieldName="employmentDetails.employerPhone"
          label="Employer Phone Number*"
          placeholder="e.g. 38 000 0000"
          options={['+353']}
          phoneExtensionName="employmentDetails.phoneExtension"
          isExtensionDisabled
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="employmentDetails.employerAddress.addressLine1">
          Employer Address Line 1*
        </Label>
        <Controller
          control={control}
          name="employmentDetails.employerAddress.addressLine1"
          render={({ field, fieldState }) => (
            <FinanceTextField
              {...field}
              {...fieldState}
              placeholder="Enter employer address line 1"
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="employmentDetails.employerAddress.addressLine2">
          Address Line 2
        </Label>
        <Controller
          control={control}
          name="employmentDetails.employerAddress.addressLine2"
          render={({ field, fieldState }) => (
            <FinanceTextField
              {...field}
              {...fieldState}
              isOptional
              placeholder="Enter employer address line 2"
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="employmentDetails.employerAddress.town">Town*</Label>
        <Controller
          control={control}
          name="employmentDetails.employerAddress.town"
          render={({ field, fieldState }) => (
            <FinanceTextField
              {...field}
              {...fieldState}
              placeholder="Enter town"
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="employmentDetails.employerAddress.county">
          County*
        </Label>
        <Controller
          control={control}
          name="employmentDetails.employerAddress.county"
          render={({ field, fieldState }) => (
            <FinanceTextField
              {...field}
              {...fieldState}
              placeholder="Enter county"
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="employmentDetails.employerAddress.eircode">
          Eircode
        </Label>
        <Controller
          control={control}
          name="employmentDetails.employerAddress.eircode"
          render={({ field, fieldState }) => (
            <FinanceTextField
              {...field}
              {...fieldState}
              isOptional
              placeholder="Enter eircode"
            />
          )}
        />
        <Spacer marginBottom="S4" />
        <Label htmlFor="employmentDetails.timeWithEmployer.years">
          Time at Current Employer*
        </Label>
        <Controller
          control={control}
          name="employmentDetails.timeWithEmployer.years"
          render={({ field, fieldState }) => (
            <FinanceSelect
              {...field}
              {...fieldState}
              placeholderAsOption
              options={CLOSE_BROTHERS_TIME_AT_EMPLOYER}
            />
          )}
        />
      </CollapsiblePanel>
    </CollapsiblePanelProvider>
  );
};

export { EmploymentDetails };
